import { z } from 'zod';
import { ApplicationOutcome } from './application';

/**
 * Cross-run skip-list partition over `ApplicationOutcome`. An outcome is
 * "permanent" when the next run would reach the same verdict — so the worker
 * can skip the card and dismiss it from LinkedIn's recommended feed without
 * burning LLM + ATS time.
 *
 * - `applied` — already submitted; LinkedIn rejects re-application at the
 *   `/v1/applications` write anyway, but the LLM evaluator round-trip is
 *   the expensive thing to save.
 * - `skipped_filter` — the pre-filter (workmode / salary / aggregator) is
 *   user-config; without a config change the verdict is stable.
 * - `skipped_already_applied` — LinkedIn marked the card as already-applied
 *   in the feed; the verdict doesn't change.
 *
 * Transient outcomes are intentionally NOT permanent:
 * - `failed_form`, `failed_login`, `failed_other` — failures are our code
 *   bugs (per legacy `apps/legacy/src/applicator/job-history.ts:21-23`); the
 *   next run should try again with the fixed code.
 * - `pending_verification`, `needs_user_completion` — still in-flight on
 *   the user side; the worker will pick them back up via the verification
 *   queue or user prompt.
 *
 * Audit finding 5.8 — restored from `loadProcessedJobIds()` legacy behavior.
 */
export const PERMANENT_OUTCOMES = [
  'applied',
  'skipped_filter',
  'skipped_already_applied',
] as const satisfies readonly z.infer<typeof ApplicationOutcome>[];

export type PermanentApplicationOutcome = (typeof PERMANENT_OUTCOMES)[number];

export function isPermanentOutcome(o: z.infer<typeof ApplicationOutcome>): boolean {
  return (PERMANENT_OUTCOMES as readonly string[]).includes(o);
}

/**
 * `GET /v1/job-history/processed?since=<ISO>&limit=<n>`
 *
 *  - `since` is an ISO-8601 timestamp; only applications with
 *    `appliedAt > since` are considered. Omitted = scan from epoch (caller
 *    pays the full-history cost). Worker default is ~90 days back.
 *  - `limit` bounds the response set. Cap mirrors the DDB-side scan ceiling
 *    so a degenerate user with 50k+ rows can't blow up the Lambda response.
 */
export const ListProcessedJobIdsQuery = z
  .object({
    since: z.string().datetime().optional(),
    limit: z.coerce.number().int().positive().max(10000).optional(),
  })
  .strict();
export type ListProcessedJobIdsQuery = z.infer<typeof ListProcessedJobIdsQuery>;

export const ListProcessedJobIdsResponse = z.object({
  jobIds: z.array(z.string()),
});
export type ListProcessedJobIdsResponse = z.infer<typeof ListProcessedJobIdsResponse>;
